import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bus, Truck, GraduationCap, ArrowUpRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface Product {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string;
  variant: 'employee' | 'trucks' | 'scmt';
  span: string;
  number: string;
  href: string;
}

const products: Product[] = [
  {
    icon: Bus,
    title: 'Employee',
    subtitle: 'Transport',
    description: 'Corporate commute management with automated routing and real-time tracking',
    variant: 'employee',
    span: 'col-span-1',
    number: '01',
    href: '/employee-transport'
  },
  {
    icon: Truck,
    title: 'Freight',
    subtitle: 'FTL · PTL · Lease',
    description: 'Complete freight management for all truck types including FTL, PTL, and leasing options',
    variant: 'trucks',
    span: 'col-span-1',
    number: '02',
    href: '/trucks'
  },
  {
    icon: GraduationCap,
    title: 'SCMT',
    subtitle: 'School Transport',
    description: 'Student transport management with real-time tracking, parent notifications, and safer daily operations',
    variant: 'scmt',
    span: 'col-span-1',
    number: '03',
    href: '/scmt'
  }
];

const BentoProductCard: React.FC<{ product: Product; index: number }> = ({ product, index }) => {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const Icon = product.icon;

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const trigger = ScrollTrigger.create({
      trigger: card,
      start: 'top 85%',
      onEnter: () => setIsVisible(true),
      once: true
    });

    return () => trigger.kill();
  }, []);

  const getGradient = () => {
    switch (product.variant) {
      case 'employee':
        return 'from-blue-500/10 to-blue-600/5';
      case 'trucks':
        return 'from-orange-500/10 to-orange-600/5';
      case 'scmt':
        return 'from-green-500/10 to-green-600/5';
      default:
        return 'from-gray-500/10 to-gray-600/5';
    }
  };

  const getIconColor = () => {
    switch (product.variant) {
      case 'employee':
        return 'bg-blue-500 text-white';
      case 'trucks':
        return 'bg-orange-500 text-white';
      case 'scmt':
        return 'bg-green-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <Link
      to={product.href}
      ref={cardRef}
      className={`
        relative group cursor-pointer block
        transition-all duration-700 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
      `}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className={`
        relative h-full min-h-[280px] p-8 rounded-3xl
        bg-gradient-to-br ${getGradient()}
        border border-gray-100
        hover:border-gray-200
        transition-all duration-500
        overflow-hidden
      `}>
        {/* Number */}
        <div className="absolute top-6 left-6 text-6xl font-bold text-gray-100 font-heading">
          {product.number}
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col">
          {/* Icon */}
          <div className={`
            w-14 h-14 rounded-2xl ${getIconColor()}
            flex items-center justify-center
            mb-6 shadow-sm
            group-hover:scale-110 group-hover:shadow-md transition-all duration-500
          `}>
            <Icon className="w-7 h-7" />
          </div>

          {/* Title */}
          <div className="mb-4">
            <h3 className="text-2xl font-bold text-navy-950 font-heading leading-tight">
              {product.title}
            </h3>
            <p className="text-lg text-gray-500 font-heading">
              {product.subtitle}
            </p>
          </div>

          {/* Description */}
          <p className="text-gray-600 mb-auto leading-relaxed">
            {product.description}
          </p>

          {/* Arrow */}
          <div className="mt-6 flex items-center gap-2 text-navy-950 font-semibold group-hover:gap-4 transition-all duration-300">
            <span>Explore</span>
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
          </div>
        </div>

        {/* Hover Glow */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent" />
        </div>
      </div>
    </Link>
  );
};

const BentoProductSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mb-16">
          <div>
            <p className="text-sm text-gray-500 mb-4 uppercase tracking-widest font-medium">
              Our Solutions
            </p>
            <h2 className="text-5xl lg:text-7xl font-bold text-navy-950 font-heading leading-[0.9]">
              ONE<br />
              <span className="text-primary-600">PLATFORM</span>
            </h2>
          </div>
          <div className="flex items-end">
            <p className="text-xl text-gray-600 leading-relaxed max-w-md">
              Three powerful solutions designed to streamline every aspect of your fleet operations
            </p>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {products.map((product, index) => (
            <BentoProductCard key={product.variant} product={product} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BentoProductSection;
